// src/components/bxpart/BxpartCreate.jsx
import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import api from "../../axiosConfig";
import { AsyncPaginate } from "react-select-async-paginate";
import { components } from "react-select";
import { authFetch } from "../utils/authFetch";

const LIMIT = 20;
const TIP_OPTIONS = [
  { value: 1, label: "Servicii primite" },
  { value: 2, label: "Obiecte" },
  { value: 3, label: "Servicii prestate" },
];

const HighlightedOption = (props) => {
  const q = (props.selectProps.inputValue || "").trim();
  const label = props.data.label || "";
  const parts = q ? label.split(new RegExp(`(${q})`, "i")) : [label];
  return (
    <components.Option {...props}>
      {parts.map((p, i) =>
        q && p.toLowerCase() === q.toLowerCase() ? <mark key={i}>{p}</mark> : <span key={i}>{p}</span>
      )}
    </components.Option>
  );
};

function useLoader(resource, makeLabel) {
  return useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      const skip = (page - 1) * LIMIT;
      try {
        // trailing slash important
        const data = await authFetch(
          `/${resource}/?search=${encodeURIComponent(q)}&skip=${skip}&limit=${LIMIT}`
        );
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
          ? data.data
          : [];
        const total =
          typeof data?.total === "number"
            ? data.total
            : typeof data?.count === "number"
            ? data.count
            : typeof data?.total_count === "number"
            ? data.total_count
            : undefined;

        const options = items.map((it) => ({
          value: it.id,
          label: makeLabel ? makeLabel(it) : it.denumire || it.cod || it.code || `#${it.id}`,
          __raw: it,
        }));

        const hasMore =
          typeof total === "number" ? skip + items.length < total : items.length === LIMIT;

        return { options, hasMore, additional: { page: page + 1 } };
      } catch (err) {
        console.error("loadOptions error", resource, err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [resource, makeLabel]
  );
}

export default function BxpartCreate() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    cod: "",
    denumire: "",
    um_id: null,
    grupa_produse_id: null,
    tip_grupa_produse: 0,
    cpv: "",
    nc8: "",
    ean: "",
    kg_net: 0,
    kg_brut: 0,
  });

  // state pentru selecturile async
  const [optUM, setOptUM] = useState(null);
  const [optGrupa, setOptGrupa] = useState(null);

  const makeUmLabel = useCallback(
    (u) => [u.cod || u.code || "", u.denumire || u.name || ""].filter(Boolean).join(" — ") || `UM #${u.id}`,
    []
  );
  const makeGrupaLabel = useCallback(
    (g) => [g.code || g.cod || "", g.denumire || g.name || ""].filter(Boolean).join(" — ") || `Grupă #${g.id}`,
    []
  );

  const loadUmOptions = useLoader("bxums", makeUmLabel);
  const loadGrupaOptions = useLoader("grupa_produse", makeGrupaLabel);
  const additional = useMemo(() => ({ page: 1 }), []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: checked }));
    } else if (type === "number") {
      setForm((f) => ({ ...f, [name]: value === "" ? "" : Number(value) }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const payload = {
        ...form,
        um_id: form.um_id ? Number(form.um_id) : null,
        grupa_produse_id: form.grupa_produse_id ? Number(form.grupa_produse_id) : null,
        tip_grupa_produse: Number(form.tip_grupa_produse) || 0,
        kg_net: Number(form.kg_net) || 0,
        kg_brut: Number(form.kg_brut) || 0,
      };
      await api.post("/bxpart", payload); // sau authFetch("/bxpart/", { method: "POST", body: JSON.stringify(payload) })
      navigate("/bxpart");
    } catch (err) {
      alert("Eroare la creare: " + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="mt-4">
      <Card className="p-3">
        <h3>Adaugă produs</h3>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cod</Form.Label>
                <Form.Control
                  type="text"
                  name="cod"
                  value={form.cod ?? ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Denumire</Form.Label>
                <Form.Control
                  type="text"
                  name="denumire"
                  value={form.denumire ?? ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            {/* UM dropdown */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>UM</Form.Label>
                <AsyncPaginate
                  value={optUM}
                  loadOptions={loadUmOptions}
                  onChange={(opt) => {
                    setOptUM(opt);
                    setForm((f) => ({ ...f, um_id: opt ? Number(opt.value) : null }));
                  }}
                  isClearable
                  placeholder="Caută UM..."
                  debounceTimeout={300}
                  cacheOptions
                  defaultOptions
                  openMenuOnFocus
                  additional={additional}
                  components={{ Option: HighlightedOption }}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>
            </Col>

            {/* Grupa produse dropdown */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Grupă produse</Form.Label>
                <AsyncPaginate
                  value={optGrupa}
                  loadOptions={loadGrupaOptions}
                  onChange={(opt) => {
                    setOptGrupa(opt);
                    setForm((f) => ({
                      ...f,
                      grupa_produse_id: opt ? Number(opt.value) : null,
                      tip_grupa_produse: opt?.__raw?.tip_grupa ?? f.tip_grupa_produse,
                    }));
                  }}
                  isClearable
                  placeholder="Caută grupă..."
                  debounceTimeout={300}
                  cacheOptions
                  defaultOptions
                  openMenuOnFocus
                  additional={additional}
                  components={{ Option: HighlightedOption }}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tip produs</Form.Label>
                <Form.Select
                  name="tip_grupa_produse"
                  value={String(form.tip_grupa_produse ?? "")}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      tip_grupa_produse: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                  required
                >
                  <option value="">— alege —</option>
                  {TIP_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cod CPV</Form.Label>
                <Form.Control
                  type="text"
                  name="cpv"
                  value={form.cpv ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cod vamal (NC8)</Form.Label>
                <Form.Control
                  type="text"
                  name="nc8"
                  value={form.nc8 ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cod de bare (EAN)</Form.Label>
                <Form.Control
                  type="text"
                  name="ean"
                  value={form.ean ?? ""}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Greutate netă (kg)</Form.Label>
                <Form.Control
                  type="number"
                  name="kg_net"
                  value={form.kg_net ?? 0}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Greutate brută (kg)</Form.Label>
                <Form.Control
                  type="number"
                  name="kg_brut"
                  value={form.kg_brut ?? 0}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="text-end">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Se salvează…" : "Salvează"}
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
